"use client"

import * as React from "react"
import { Search, X, ChevronDown } from "lucide-react"
import { TIER_FILTERS, SORT_OPTIONS, type Sort, type TierFilter } from "./types"

interface BrandsToolbarProps {
  query: string
  setQuery: (v: string) => void
  tier: TierFilter
  setTier: (v: TierFilter) => void
  sort: Sort
  setSort: (v: Sort) => void
  totalCount: number
  shownCount: number
}

/**
 * Brands page search + filter card.
 *
 * White rounded card with thin gray border, three stacked rows:
 *
 *   Row 1 — Search input pill (full-width).
 *   Row 2 — Tier filter pills (flat — All / Popular / Luxury / Value /
 *           Budget / Niche) on the left · "Sort" label + dropdown on
 *           the right.
 *   Row 3 — `border-t` + result-count line ("Showing N of M brands").
 *
 * Sticky positioning lives on a wrapper above the card so the rest of the
 * page can scroll behind the card while the card stays in view (top-[92px]
 * = 86px header + 6px orange border).
 */
export function BrandsToolbar({
  query,
  setQuery,
  tier,
  setTier,
  sort,
  setSort,
  totalCount,
  shownCount,
}: BrandsToolbarProps) {
  return (
    <div className="pt-4">
      <div className="mx-auto w-full max-w-[1280px] px-6 sm:px-8 lg:px-12">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-[0_4px_16px_-8px_rgba(0,0,0,0.06)]">
          {/* Row 1 — Search + View toggle */}
          <div className="flex flex-wrap items-center gap-4 px-5 pt-5">
            <div className="relative min-w-0 flex-1">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
              />
              <input
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={`Search ${totalCount.toLocaleString()} brands by name…`}
                aria-label="Search brands"
                className="h-11 w-full rounded-full border border-gray-200 bg-white pl-11 pr-11 text-sm text-gray-900 placeholder:text-gray-500 focus:border-[#ff9951] focus:outline-none focus:ring-[3px] focus:ring-[#ff9951]/15"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 grid h-[22px] w-[22px] -translate-y-1/2 place-items-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </div>
          </div>

          {/* Row 2 — Tier filter pills + Sort */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div
              className="flex min-w-0 flex-wrap items-center gap-2"
              role="tablist"
              aria-label="Filter by tier"
            >
              {TIER_FILTERS.map(t => {
                const active = t.key === tier
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTier(t.key)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-[13px] font-semibold transition-all duration-150 ${
                      active
                        ? "bg-[#ff9951] text-[var(--color-brand-action-text)] shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900"
                    }`}
                  >
                    {t.label}
                  </button>
                )
              })}
            </div>

            <div className="flex flex-shrink-0 items-center gap-2">
              <label
                htmlFor="brands-sort"
                className="text-[13px] font-semibold text-gray-600"
              >
                Sort
              </label>
              <div className="relative">
                <select
                  id="brands-sort"
                  value={sort}
                  onChange={e => setSort(e.target.value as Sort)}
                  aria-label="Sort brands"
                  className="h-10 cursor-pointer appearance-none rounded-full border border-gray-200 bg-white py-1.5 pl-4 pr-9 text-[13px] font-semibold text-gray-900 focus:border-[#ff9951] focus:outline-none focus:ring-[3px] focus:ring-[#ff9951]/15"
                >
                  {SORT_OPTIONS.map(s => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  aria-hidden
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Row 3 — bordered count footer */}
          <div className="border-t border-gray-200 px-5 py-3 text-[13px] text-gray-500">
            Showing <strong className="font-bold text-gray-900">{shownCount.toLocaleString()}</strong>{" "}
            of {totalCount.toLocaleString()} brands
          </div>
        </div>
      </div>
    </div>
  )
}
